import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CameraIcon, CheckIcon, ImageIcon, RotateCcwIcon, Share2Icon } from 'lucide-react';
import { PoseGuideOverlay } from '../../components/engagement/PoseGuideOverlay';
import { ShareSheet } from '../../components/engagement/ShareSheet';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Modal } from '../../components/ui/Modal';
import { Switch } from '../../components/ui/Switch';
import { communityChallenges, progressPhotos } from '../../data/engagement';
import { useLocale } from '../../contexts/LocaleContext';

/**
 * MOB/Community/ChallengePhoto — optional photo for a challenge with the "Guia de pose".
 * Camera is simulated in the prototype. Nothing is analysed, scored or published.
 */
export function ChallengePhoto() {
  const {
    challengeId
  } = useParams();
  const {
    t,
    tl
  } = useLocale();
  const challenge = communityChallenges.find(item => item.id === challengeId);
  const [guideOn, setGuideOn] = useState(true);
  const [opacity, setOpacity] = useState(45);
  const [photo, setPhoto] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [confirmShare, setConfirmShare] = useState(false);
  const [sharing, setSharing] = useState(false);
  if (!challenge || !challenge.poseGuide) {
    return <div className="px-2 pb-2" data-id="element-2682" data-mp="341q6iccd8kes">
        <PageHeader title={t('eng.pose.title')} showBack data-id="element-2683" data-mp="340ot417vnltw" />
        <Card className="p-3" data-id="element-2684" data-mp="2z4pz9y3s5bm3">
          <p className="text-[13px] text-muted" data-id="element-2685" data-mp="2z4hjnyg9vs77">{t('eng.pose.missing')}</p>
        </Card>
      </div>;
  }
  const poseName = t(`eng.pose.pose.${challenge.poseGuide}`);
  /** Prototype stand-in image for a captured / chosen photo. */
  const sampleImage = progressPhotos[0]?.url ?? null;
  return <div className="px-2 pb-2" data-id="element-2686" data-mp="1iqrhix8pre07">
      <PageHeader title={t('eng.pose.title')} subtitle={t('eng.pose.subtitle')} showBack data-id="element-2687" data-mp="1eo35kgopdqz0" />

      <p className="mb-2 text-[12px] font-medium text-deep" data-id="element-2688" data-mp="1g50gotabbsim">
        {tl(challenge.title)} · {poseName}
      </p>

      <div role="img" aria-label={t('eng.pose.viewfinder', {
      pose: poseName
    })} className="relative aspect-[3/4] w-full overflow-hidden rounded-card bg-deep" data-id="element-2689" data-mp="1l4mvh75shgtj">
        {photo ? <img src={photo} alt="" className="absolute inset-0 h-full w-full object-cover" data-id="element-2690" data-mp="1b54emzl6eb5p" /> : <span className="bee-overline absolute left-2 top-2 text-[8.5px] font-bold text-sand/70" data-id="element-2691" data-mp="xdhgeu7oaska">
            {t('eng.pose.camera')}
          </span>}
        {guideOn && <PoseGuideOverlay pose={challenge.poseGuide} opacity={opacity / 100} data-id="element-2692" data-mp="ngl6ql61g0e5" />}
      </div>

      <Card className="mt-2 p-2" data-id="element-2693" data-mp="sffnlhkg3evm">
        <div className="flex items-center justify-between gap-2" data-id="element-2694" data-mp="sghacjatppt6">
          <span className="min-w-0" data-id="element-2695" data-mp="efwpzip9flxk">
            <span className="block text-[13px] font-bold text-deep" data-id="element-2696" data-mp="egy27qi0iegv">{t('eng.pose.guide')}</span>
            <span className="block text-[11.5px] leading-snug text-muted" data-id="element-2697" data-mp="ig6vku954abw">{t('eng.pose.guideHint')}</span>
          </span>
          <Switch checked={guideOn} onChange={setGuideOn} label={t('eng.pose.guide')} data-id="element-2698" data-mp="3vyltupom61ms" />
        </div>
        {guideOn && <label className="mt-2 block" data-id="element-2699" data-mp="3r1lo6fvof0w7">
            <span className="flex items-center justify-between text-[12px] text-deep" data-id="element-2700" data-mp="8tw4wcobdb4l">
              {t('eng.pose.opacity')}
              <span className="bee-num text-muted" data-id="element-2701" data-mp="3szurhddcsqq">{opacity}%</span>
            </span>
            <input type="range" min={15} max={85} step={5} value={opacity} onChange={event => setOpacity(Number(event.target.value))} className="mt-1 h-11 w-full accent-[#C89432]" data-id="element-2702" data-mp="iqiqx4sdm887" />
          </label>}
        <p className="mt-1 text-[11px] leading-snug text-muted" data-id="element-2703" data-mp="njbuhpc3zaip">{t('eng.pose.disclaimer')}</p>
      </Card>

      <div className="mt-2 grid grid-cols-2 gap-2" data-id="element-2704" data-mp="11g77fsvhdm41">
        {photo ? <Button variant="outline" onClick={() => {
        setPhoto(null);
        setSaved(false);
      }} data-id="element-2705" data-mp="wkjer3hsiats">
            <RotateCcwIcon size={15} strokeWidth={1.6} aria-hidden="true" data-id="element-2706" data-mp="1kmep3g0dqpwf" />
            {t('eng.pose.retake')}
          </Button> : <Button variant="primary" onClick={() => setPhoto(sampleImage)} data-id="element-2707" data-mp="1pe6hzx70d7pq">
            <CameraIcon size={15} strokeWidth={1.6} aria-hidden="true" data-id="element-2708" data-mp="1ubogsurba6k0" />
            {t('eng.pose.capture')}
          </Button>}
        <Button variant="outline" onClick={() => setPhoto(sampleImage)} data-id="element-2709" data-mp="xojv0yhnd4y0">
          <ImageIcon size={15} strokeWidth={1.6} aria-hidden="true" data-id="element-2710" data-mp="11ou1sdd9jzdx" />
          {t('eng.pose.choose')}
        </Button>
      </div>

      <div className="mt-2 space-y-2" data-id="element-2711" data-mp="16p8azyqlpfdj">
        {!photo && <p className="text-[12px] text-muted" data-id="element-2712" data-mp="1bmi53roxqf7p">{t('eng.pose.noPhoto')}</p>}
        {photo && <Button variant="primary" size="lg" fullWidth disabled={saved} onClick={() => setSaved(true)} data-id="element-2713" data-mp="2i33nt0u19c1l">
            {saved && <CheckIcon size={15} aria-hidden="true" data-id="element-2714" data-mp="2n03undypiitr" />}
            {t('eng.pose.save')}
          </Button>}
        {saved && <>
            <p role="status" className="rounded-card border border-sand-line bg-sand-deep/60 p-2 text-[12px] text-deep" data-id="element-2715" data-mp="6eyrrdx31iow">
              {t('eng.pose.saved')}
            </p>
            <Button variant="outline" size="lg" fullWidth onClick={() => setConfirmShare(true)} data-id="element-2716" data-mp="1m5pcwp36mfz">
              <Share2Icon size={15} aria-hidden="true" data-id="element-2717" data-mp="3su9v42l5fxco" />
              {t('eng.pose.share')}
            </Button>
          </>}
        <p className="text-[11px] text-muted" data-id="element-2718" data-mp="3ju4v4diac18j">{t('eng.pose.prototypeNote')}</p>
      </div>

      <Modal open={confirmShare} onClose={() => setConfirmShare(false)} labelledBy="pose-share-title" data-id="element-2719" data-mp="3eusdc769tkct">
        <h2 id="pose-share-title" className="pr-5 text-section text-deep" data-id="element-2720" data-mp="39vxho4n7g1ba">
          {t('eng.pose.shareTitle')}
        </h2>
        <p className="mt-1 text-[13px] leading-relaxed text-muted" data-id="element-2721" data-mp="1d3nmhdc5wntj">{t('eng.pose.shareBody')}</p>
        <div className="mt-3 space-y-2" data-id="element-2722" data-mp="1842nj2wzsfgt">
          <Button variant="primary" size="lg" fullWidth onClick={() => {
          setConfirmShare(false);
          setSharing(true);
        }} data-id="element-2723" data-mp="1853v2xesujts">
            {t('eng.pose.shareConfirm')}
          </Button>
          <Button variant="outline" size="lg" fullWidth onClick={() => setConfirmShare(false)} data-id="element-2724" data-mp="2sno66j4wgr3t">
            {t('eng.pose.shareCancel')}
          </Button>
        </div>
      </Modal>

      <ShareSheet open={sharing} onClose={() => setSharing(false)} data-id="element-2725" data-mp="3lar3x91r4xt0" />
    </div>;
}